/** Phase 4 — Network DAO MVP types (off-chain signed-vote stack). */

export enum ProposalStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  PASSED = 'passed',
  REJECTED = 'rejected',
  EXECUTED = 'executed',
  CANCELLED = 'cancelled',
}

export enum ProposalCategory {
  GOVERNANCE = 'governance',
  TREASURY = 'treasury',
  LISTINGS = 'listings',
  PARTNERSHIP = 'partnership',
  PARAMETER = 'parameter',
  OTHER = 'other',
}

export enum ProposalExecutionType {
  ON_CHAIN = 'on-chain',
  OFF_CHAIN = 'off-chain',
  INFORMATIONAL = 'informational',
}

export enum ExecutionRecordKind {
  ON_CHAIN = 'on-chain',
  OFF_CHAIN_ACTION = 'off-chain-action',
  INFORMATIONAL = 'informational',
}

export enum VoteChoice {
  YES = 'yes',
  NO = 'no',
  ABSTAIN = 'abstain',
}

export enum DecisionLogEvent {
  CREATED = 'created',
  VOTED = 'voted',
  FINALISED = 'finalised',
  EXECUTION_RECORDED = 'execution-recorded',
  CANCELLED = 'cancelled',
}

export interface ProposalParams {
  quorumPercent: number
  passThresholdPercent: number
  alpha: number
  beta: number
  gamma: number
  minMproUnits: number
  minInodeCount: number
  thresholdMode: 'OR' | 'AND'
}

export interface FinalTally {
  yes: number
  no: number
  abstain: number
  totalVotedVP: number
  quorumReached: boolean
  pass: boolean
}

export interface ExecutionRecord {
  recordedAt: string
  recordedBy: string
  executionKind: ExecutionRecordKind
  txHash?: string
  description: string
}

export interface Proposal {
  proposalId: string
  title: string
  bodyMarkdown: string
  proposerWallet: string
  category: ProposalCategory
  executionType: ProposalExecutionType
  status: ProposalStatus
  snapshotBlock: number
  snapshotChainId: number
  votingStartTimestamp: number
  votingEndTimestamp: number
  params: ProposalParams
  totalCirculatingVPSnapshot: number
  finalisedAt?: string
  finalTally?: FinalTally
  executionRecord?: ExecutionRecord
  createdAt?: string
  updatedAt?: string
}

export interface ProposalTally {
  proposalId: string
  yes: number
  no: number
  abstain: number
  totalVotedVP: number
  quorumPercent: number
  quorumReached: boolean
  passing: boolean
  totalCirculatingVPSnapshot: number
  /** How many different wallets voted — the floor that replaced the quorum. */
  distinctVoters?: number
  /** Each floor and whether it is met, so a screen can say what is missing. */
  floors?: {
    minDistinctVoters: number | null
    minVotedVpPercent: number | null
    distinctVotersMet: boolean
    votedVpMet: boolean
  }
  /** What the clan multiplier added, and to which side. */
  clanBoosts?: {
    clanId: string
    name: string | null
    choice: 'yes' | 'no' | 'abstain'
    boostedWallets: number
    addedVP: number
  }[]
}

export interface ProposalDecisionLogEntry {
  logId: string
  proposalId: string
  actor: string
  event: DecisionLogEvent
  payload?: string
  occurredAt: string
}

export interface ProposalListResponse {
  count: number
  data: Proposal[]
}

export interface ProposalDetailResponse {
  proposal: Proposal
  tally: ProposalTally
  myVote?: { choice: VoteChoice; votingPowerSnapshot: number }
  decisionLog: ProposalDecisionLogEntry[]
}

export interface DaoEligibilityDetails {
  wallet: string
  snapshotBlock: number
  chainId: number
  mproUnits: number
  inodeCount: number
  ggnodeCount: number
  minMproUnits: number
  minInodeCount: number
  thresholdMode: 'OR' | 'AND'
}

export interface DaoEligibilityResult {
  ok: boolean
  reason?: string
  details: DaoEligibilityDetails
}

/** Payload signed via EIP-712 by the voter's wallet. */
export interface DaoVotePayload {
  proposalId: string
  choice: VoteChoice
  snapshotBlock: number
  voterWallet: string
  castAt: number
}

/** EIP-712 typed-data definition the frontend hands to wagmi. */
export const DAO_EIP712_DOMAIN_NAME = 'MPRO Lab DAO'
export const DAO_EIP712_DOMAIN_VERSION = '1'
export const DAO_EIP712_VERIFYING_CONTRACT =
  '0x0000000000000000000000000000000000000000'

export const DAO_EIP712_VOTE_TYPES = {
  Vote: [
    { name: 'proposalId', type: 'string' },
    { name: 'choice', type: 'string' },
    { name: 'snapshotBlock', type: 'uint256' },
    { name: 'voterWallet', type: 'address' },
    { name: 'castAt', type: 'uint256' },
  ],
} as const

export const DAO_SIGNED_MESSAGE_VERSION_V1 = 'eip712-v1'
